import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { Plus, Trash2, ArrowLeft, ImagePlus, FileText, X } from "lucide-react";
import { usePartnerPageMeta } from "@/components/partner/PartnerWorkspaceShell";
import { usePartnerData } from "@/hooks/usePartnerData";
import { isPartnerProfileComplete } from "@/lib/partnerProfileComplete";
import {
  APPLICATION_REQUIREMENT_OPTIONS,
  APPLICATION_REQUIREMENT_OTHER,
  parseApplicationRequirements,
  serializeApplicationRequirements,
  type ApplicationRequirementSpec,
} from "@/data/applicationRequirementCatalog";
import { syncProgramDeadline } from "@/lib/partnerGoogleCalendar";

const PROGRAM_IMAGE_BUCKET = "program-images";
const PROGRAM_BROCHURE_BUCKET = "program-brochures";
const MAX_PROGRAM_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_PROGRAM_BROCHURE_BYTES = 15 * 1024 * 1024;
const ALLOWED_PROGRAM_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

function sanitizeFileName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

/** Parse optional decimal fields (cost / award). Empty → null; invalid → throw. */
function parseOptionalNumber(raw: string, label: string): number | null {
  const value = raw?.toString().trim() ?? "";
  if (!value) return null;
  const normalized = value.replace(/,/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
    throw new Error(`${label} must be a number (got "${raw}").`);
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) {
    throw new Error(`${label} must be a number (got "${raw}").`);
  }
  return n;
}

/** Parse optional integer fields (duration / grades). Empty → null; invalid → throw. */
function parseOptionalInt(raw: string, label: string): number | null {
  const value = raw?.toString().trim() ?? "";
  if (!value) return null;
  if (!/^-?\d+$/.test(value)) {
    throw new Error(`${label} must be a whole number (got "${raw}").`);
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    throw new Error(`${label} must be a whole number (got "${raw}").`);
  }
  return n;
}

const PartnerAddProgram = () => {
  const { id } = useParams();
  const [step, setStep] = useState(1);
  const [benefits, setBenefits] = useState([""]);
  const [requirements, setRequirements] = useState([""]);
  const [applicationRequirements, setApplicationRequirements] = useState<
    ApplicationRequirementSpec[]
  >([{ key: "academic_exam" }]);
  const [formData, setFormData] = useState({
    title: "",
    program_subtitle: "",
    description: "",
    category: "scholarships",
    type: "all",
    funding_type: "fullyFunded",
    location: "",
    long_description: "",
    application_deadline: "",
    application_url: "",
    external_url: "",
    image_url: "",
    cost: "",
    duration_days: "",
    duration_type: "weeks",
    program_format: "",
    selectivity_tier: "",
    subject_areas: "",
    program_type: "",
    award_amount_usd: "",
    citizenship_req: "",
    application_dependency: "",
    brochure_url: "",
    faculty_info: "",
    eligibility_requirements: "",
    program_benefits: "",
    application_requirements: "",
    grade_level_min: "",
    grade_level_max: "",
  });
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [brochureName, setBrochureName] = useState("");
  const [brochureUploading, setBrochureUploading] = useState(false);
  const { user } = useAuth();
  const { profile, loading: profileLoading } = usePartnerData();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const isVerified = Boolean(profile?.is_verified);

  const backAction = useMemo(
    () => (
      <Link to="/partners/programs">
        <Button variant="outline" size="sm" className="rounded-xl gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </Link>
    ),
    []
  );

  usePartnerPageMeta({
    title: isEditing ? "Edit program" : "Create program",
    subtitle: isEditing
      ? "Update details students see in Discover"
      : "Publish for high school students on Skutopia Discover",
    action: backAction,
  });

  // Load program data when editing
  useEffect(() => {
    if (id) {
      loadProgramData(id);
    }
  }, [id]);

  const verificationBlockedMessage = isPartnerProfileComplete(profile)
    ? "Your account is awaiting admin verification before you can publish listings."
    : "Complete and save your organization profile, then wait for verification to publish listings.";

  // Only verified partners may create new listings (edits still allowed).
  useEffect(() => {
    if (isEditing || profileLoading) return;
    if (!isVerified) {
      toast.error(verificationBlockedMessage);
      navigate("/partners/organization", { replace: true });
    }
  }, [
    isEditing,
    isVerified,
    profileLoading,
    verificationBlockedMessage,
    navigate,
  ]);

  const loadProgramData = async (programId: string) => {
    try {
      const { data, error } = await supabase
        .from("programs")
        .select("*")
        .eq("id", programId)
        .single();

      if (error) throw error;
      if (data) {
        // Parse JSON arrays
        const parsedEligibility = typeof data.eligibility_requirements === 'string'
          ? JSON.parse(data.eligibility_requirements)
          : (data.eligibility_requirements || []);
        const parsedBenefits = typeof data.program_benefits === 'string'
          ? JSON.parse(data.program_benefits)
          : (data.program_benefits || []);
        const parsedApplicationReqs = parseApplicationRequirements(
          typeof data.application_requirements === "string"
            ? JSON.parse(data.application_requirements)
            : data.application_requirements
        );

        setRequirements(parsedEligibility.length > 0 ? parsedEligibility : [""]);
        setBenefits(parsedBenefits.length > 0 ? parsedBenefits : [""]);
        setApplicationRequirements(
          parsedApplicationReqs.length > 0
            ? parsedApplicationReqs
            : [{ key: "academic_exam" }]
        );

        setFormData({
          title: data.title || "",
          program_subtitle: data.subtitle || "",
          description: data.description || "",
          category: data.category || "scholarships",
          type: "all",
          funding_type: data.funding_type || "fullyFunded",
          location: data.location || "",
          long_description: data.long_description || "",
          application_deadline: (data.application_deadline || "").toString().slice(0, 10),
          application_url: data.application_url || "",
          external_url: data.external_url || "",
          image_url: data.image_url || "",
          cost: data.cost || "",
          duration_days: data.duration_days || "",
          duration_type: data.duration_type || "years",
          program_format: data.program_format || "",
          selectivity_tier: data.selectivity_tier || "",
          subject_areas: Array.isArray(data.subject_areas)
            ? data.subject_areas.join(", ")
            : typeof data.subject_areas === "string"
              ? data.subject_areas
              : "",
          program_type: data.program_type || "",
          award_amount_usd: data.award_amount_usd || "",
          citizenship_req: data.citizenship_req || "",
          application_dependency: data.application_dependency || "",
          brochure_url: data.brochure_url || "",
          faculty_info: data.faculty_info || "",
          eligibility_requirements: data.eligibility_requirements || "",
          program_benefits: data.program_benefits || "",
          application_requirements: data.application_requirements || "",
          grade_level_min: data.grade_level_min || "",
          grade_level_max: data.grade_level_max || "",
        });
        setImageFile(null);
        setImagePreview(data.image_url || "");
        setBrochureFile(null);
        setBrochureName(
          data.brochure_url
            ? decodeURIComponent(
                String(data.brochure_url).split("/").pop() || "Brochure.pdf"
              )
            : ""
        );
      }
    } catch (error) {
      console.error("Error loading program:", error);
      alert("Error loading program data");
    }
  };

  const handleProgramImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_PROGRAM_IMAGE_TYPES.includes(file.type)) {
      toast.error("Please choose a JPG, PNG, WEBP, or GIF image.");
      return;
    }
    if (file.size > MAX_PROGRAM_IMAGE_BYTES) {
      toast.error("Image must be 5MB or smaller.");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const clearProgramImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image_url: "" }));
  };

  const uploadProgramImage = async (file: File, partnerId: string) => {
    const ext =
      file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") ||
      "jpg";
    const base = sanitizeFileName(
      file.name.replace(/\.[^.]+$/, "") || "cover"
    );
    const filePath = `${partnerId}/${Date.now()}-${base}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from(PROGRAM_IMAGE_BUCKET)
      .upload(filePath, file, {
        upsert: false,
        contentType: file.type || undefined,
      });
    if (uploadErr) throw uploadErr;

    const {
      data: { publicUrl },
    } = supabase.storage.from(PROGRAM_IMAGE_BUCKET).getPublicUrl(filePath);
    return publicUrl;
  };

  const handleBrochureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Please upload a PDF brochure.");
      return;
    }
    if (file.size > MAX_PROGRAM_BROCHURE_BYTES) {
      toast.error("Brochure must be 15MB or smaller.");
      return;
    }

    setBrochureFile(file);
    setBrochureName(file.name);
  };

  const clearBrochure = () => {
    setBrochureFile(null);
    setBrochureName("");
    setFormData((prev) => ({ ...prev, brochure_url: "" }));
  };

  const uploadProgramBrochure = async (file: File, partnerId: string) => {
    const base = sanitizeFileName(
      file.name.replace(/\.[^.]+$/, "") || "brochure"
    );
    const filePath = `${partnerId}/${Date.now()}-${base}.pdf`;

    const { error: uploadErr } = await supabase.storage
      .from(PROGRAM_BROCHURE_BUCKET)
      .upload(filePath, file, {
        upsert: false,
        contentType: "application/pdf",
      });
    if (uploadErr) throw uploadErr;

    const {
      data: { publicUrl },
    } = supabase.storage.from(PROGRAM_BROCHURE_BUCKET).getPublicUrl(filePath);
    return publicUrl;
  };

  const addBenefit = () => setBenefits([...benefits, ""]);
  const removeBenefit = (index: number) => {
    if (benefits.length > 1) {
      const newBenefits = benefits.filter((_, i) => i !== index);
      setBenefits(newBenefits);
    }
  };
  const updateBenefit = (index: number, value: string) => {
    const newBenefits = [...benefits];
    newBenefits[index] = value;
    setBenefits(newBenefits);
  };

  const addRequirement = () => setRequirements([...requirements, ""]);
  const removeRequirement = (index: number) => {
    if (requirements.length > 1) {
      const newRequirements = requirements.filter((_, i) => i !== index);
      setRequirements(newRequirements);
    }
  };
  const updateRequirement = (index: number, value: string) => {
    const newRequirements = [...requirements];
    newRequirements[index] = value;
    setRequirements(newRequirements);
  };

  const addApplicationRequirement = () =>
    setApplicationRequirements([
      ...applicationRequirements,
      { key: "academic_exam" },
    ]);
  const removeApplicationRequirement = (index: number) => {
    if (applicationRequirements.length > 1) {
      setApplicationRequirements(
        applicationRequirements.filter((_, i) => i !== index)
      );
    }
  };
  const updateApplicationRequirementKey = (index: number, key: string) => {
    const next = [...applicationRequirements];
    next[index] = {
      key,
      label:
        key === APPLICATION_REQUIREMENT_OTHER
          ? next[index].label || ""
          : undefined,
    };
    setApplicationRequirements(next);
  };
  const updateApplicationRequirementLabel = (index: number, label: string) => {
    const next = [...applicationRequirements];
    next[index] = { ...next[index], label };
    setApplicationRequirements(next);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!user) throw new Error("User not authenticated");
      if (!isEditing && !isVerified) {
        throw new Error(verificationBlockedMessage);
      }

      const otherMissingLabel = applicationRequirements.some(
        (r) =>
          r.key === APPLICATION_REQUIREMENT_OTHER &&
          !(r.label && r.label.trim())
      );
      if (otherMissingLabel) {
        toast.error('Add a description for each "Other" requirement.');
        setLoading(false);
        return;
      }

      let imageUrl = formData.image_url?.trim() || null;
      if (imageFile) {
        setImageUploading(true);
        imageUrl = await uploadProgramImage(imageFile, user.id);
        setFormData((prev) => ({ ...prev, image_url: imageUrl || "" }));
        setImageFile(null);
        setImageUploading(false);
      }

      let brochureUrl = formData.brochure_url?.trim() || null;
      if (brochureFile) {
        setBrochureUploading(true);
        brochureUrl = await uploadProgramBrochure(brochureFile, user.id);
        setFormData((prev) => ({ ...prev, brochure_url: brochureUrl || "" }));
        setBrochureFile(null);
        setBrochureUploading(false);
      }

      let result;

      const programData = {
        title: formData.title,
        subtitle: formData.program_subtitle?.trim() || null,
        description: formData.description,
        long_description: formData.long_description || formData.description,
        category: formData.category,
        type: "all",
        funding_type: formData.funding_type,
        location: formData.location,
        // End-of-day UTC so the selected calendar day stays inclusive worldwide.
        application_deadline: formData.application_deadline
          ? `${formData.application_deadline}T23:59:59.999Z`
          : null,
        // Blank / sentinel → students apply in the Skutopia app (wallet docs attach).
        application_url: formData.application_url?.trim()
          ? formData.application_url.trim()
          : "internal-application-form",
        external_url: formData.external_url?.trim() || null,
        image_url: imageUrl,
        cost: parseOptionalNumber(formData.cost, "Cost (USD)"),
        duration_days: parseOptionalInt(formData.duration_days, "Duration"),
        duration_type: formData.duration_type || null,
        program_format: formData.program_format?.trim() || null,
        selectivity_tier: formData.selectivity_tier || null,
        subject_areas: formData.subject_areas
          ? formData.subject_areas
              .split(/[,;|]/)
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        program_type: formData.program_type?.trim() || null,
        award_amount_usd: parseOptionalNumber(
          formData.award_amount_usd,
          "Award amount (USD)"
        ),
        citizenship_req: formData.citizenship_req?.trim() || null,
        application_dependency: formData.application_dependency?.trim() || null,
        brochure_url: brochureUrl,
        faculty_info: formData.faculty_info?.trim() || null,
        eligibility_requirements: requirements.filter((r) => r.trim()),
        program_benefits: benefits.filter((b) => b.trim()),
        application_requirements: serializeApplicationRequirements(
          applicationRequirements
        ),
        grade_level_min: parseOptionalInt(formData.grade_level_min, "Min grade"),
        grade_level_max: parseOptionalInt(formData.grade_level_max, "Max grade"),
        // Ownership is always the signed-in partner account.
        partner_id: user.id,
      };

      if (isEditing && id) {
        // Update existing program
        result = await supabase
          .from("programs")
          .update(programData)
          .eq("id", id)
          .select();

        if (result.error) throw result.error;
        console.log("Program updated:", result.data);
        void syncProgramDeadline(id).catch(() => undefined);
      } else {
        // Create new program
        const programId = crypto.randomUUID();

        result = await supabase
          .from("programs")
          .insert([
            {
              id: programId,
              institution_id: user.id,
              ...programData,
              is_active: true,
            },
          ])
          .select();

        if (result.error) throw result.error;
        console.log("Program created:", result.data);
        void syncProgramDeadline(programId).catch(() => undefined);
      }

      navigate("/partners/programs");
    } catch (error: unknown) {
      console.error("Error saving program:", error);
      const rawMessage =
        error instanceof Error
          ? error.message
          : typeof error === "object" &&
              error &&
              "message" in error &&
              typeof (error as { message: unknown }).message === "string"
            ? (error as { message: string }).message
            : "";
      const status =
        typeof error === "object" &&
        error &&
        "status" in error &&
        typeof (error as { status: unknown }).status === "number"
          ? (error as { status: number }).status
          : undefined;
      const looksLikeRls =
        status === 403 ||
        /row-level security|permission denied|not allowed|42501/i.test(rawMessage);
      const message = looksLikeRls && !isEditing
        ? verificationBlockedMessage
        : rawMessage ||
          (isEditing ? "Error updating program" : "Error creating program");
      toast.error(message);
    } finally {
      setImageUploading(false);
      setBrochureUploading(false);
      setLoading(false);
    }
  };

  if (!isEditing && (profileLoading || !isVerified)) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-skutopia-600" />
      </div>
    );
  }

  return (
      <div className="max-w-3xl">
        <div className="flex gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${step >= s ? "bg-skutopia-600" : "bg-gray-200 dark:bg-gray-700"}`}
            />
          ))}
        </div>
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
              {step === 1 && (
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-4 md:p-8">
                  <h2 className="text-base md:text-xl font-semibold text-gray-900 dark:text-white mb-4 md:mb-6">
                    Program details
                  </h2>
                  <div className="space-y-4 md:space-y-6">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Program title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g., Yale Young African Scholars, Mastercard Foundation Scholars"
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Subtitle <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        name="program_subtitle"
                        value={formData.program_subtitle}
                        onChange={handleInputChange}
                        placeholder="A short tagline shown under the title, e.g. Full scholarship for STEM leaders"
                        maxLength={140}
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Short Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="What high school students will see first in Discover"
                        rows={2}
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Category
                        </label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        >
                          <option value="scholarships">Scholarships</option>
                          <option value="academics">University / academic programs</option>
                          <option value="leadership">Leadership & summer schools</option>
                          <option value="internships">Internships</option>
                          <option value="competitions">Competitions</option>
                          <option value="research">Research</option>
                          <option value="gapYear">Post High School / capacity building</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Discover audience
                        </label>
                        <div className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-xs md:text-sm text-gray-700 dark:text-gray-200">
                          <span className="font-semibold text-[#00573C]">All students</span>
                          <span className="block mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                            Programs appear in the unified Opportunities feed on Discover.
                          </span>
                        </div>
                        <input type="hidden" name="type" value="all" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Funding Type
                        </label>
                        <select
                          name="funding_type"
                          value={formData.funding_type}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        >
                          <option value="fullyFunded">Fully funded</option>
                          <option value="partial">Partial funding</option>
                          <option value="none">Self-funded / fee-based</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Location
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="City, Country"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Cost (USD)
                        </label>
                        <input
                          type="number"
                          name="cost"
                          inputMode="decimal"
                          min="0"
                          step="any"
                          value={formData.cost}
                          onChange={handleInputChange}
                          placeholder="e.g., 5000"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Award Amount (USD)
                        </label>
                        <input
                          type="number"
                          name="award_amount_usd"
                          inputMode="decimal"
                          min="0"
                          step="any"
                          value={formData.award_amount_usd}
                          onChange={handleInputChange}
                          placeholder="e.g., 10000"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Duration
                        </label>
                        <input
                          type="number"
                          name="duration_days"
                          inputMode="numeric"
                          min="0"
                          step="1"
                          value={formData.duration_days}
                          onChange={handleInputChange}
                          placeholder="e.g., 90"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Duration Type
                        </label>
                        <select
                          name="duration_type"
                          value={formData.duration_type}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        >
                          <option value="days">Days</option>
                          <option value="weeks">Weeks</option>
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Program Format
                        </label>
                        <input
                          type="text"
                          name="program_format"
                          value={formData.program_format}
                          onChange={handleInputChange}
                          placeholder="e.g., Online, In-person"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Min grade
                        </label>
                        <input
                          type="number"
                          name="grade_level_min"
                          inputMode="numeric"
                          min="1"
                          max="13"
                          step="1"
                          value={formData.grade_level_min}
                          onChange={handleInputChange}
                          placeholder="e.g. 10"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Max grade
                        </label>
                        <input
                          type="number"
                          name="grade_level_max"
                          inputMode="numeric"
                          min="1"
                          max="13"
                          step="1"
                          value={formData.grade_level_max}
                          onChange={handleInputChange}
                          placeholder="e.g. 12"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Selectivity Tier
                        </label>
                        <select
                          name="selectivity_tier"
                          value={formData.selectivity_tier}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        >
                          <option value="">Select tier</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Program Type
                        </label>
                        <input
                          type="text"
                          name="program_type"
                          value={formData.program_type}
                          onChange={handleInputChange}
                          placeholder="e.g., Internship, Scholarship"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Subjects or courses
                        </label>
                        <input
                          type="text"
                          name="subject_areas"
                          value={formData.subject_areas}
                          onChange={handleInputChange}
                          placeholder="e.g., STEM, Computer Science, Business Administration"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                          Separate multiple subjects or university courses with commas.
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                          Citizenship Requirements
                        </label>
                        <input
                          type="text"
                          name="citizenship_req"
                          value={formData.citizenship_req}
                          onChange={handleInputChange}
                          placeholder="e.g., US Citizens, International"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Brochure PDF{" "}
                        <span className="font-normal text-gray-400">(optional)</span>
                      </label>
                      <p className="mb-2 text-[11px] text-gray-500 dark:text-gray-400">
                        Upload a prospectus or program brochure (PDF · max 15MB).
                        Learners can open it from the program page.
                      </p>
                      {brochureName || formData.brochure_url ? (
                        <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-3">
                          <FileText className="h-6 w-6 text-skutopia-600 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate">
                              {brochureName || "Brochure.pdf"}
                            </p>
                            <p className="text-[11px] text-gray-500">
                              {brochureFile
                                ? "New PDF selected — uploads when you submit."
                                : "Current brochure on file."}
                            </p>
                          </div>
                          <label className="cursor-pointer rounded-lg bg-white dark:bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 shrink-0">
                            Replace
                            <input
                              type="file"
                              accept="application/pdf,.pdf"
                              className="hidden"
                              onChange={handleBrochureSelect}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={clearBrochure}
                            className="rounded-lg p-1.5 text-gray-500 hover:bg-white dark:hover:bg-gray-900 shrink-0"
                            aria-label="Remove brochure"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 px-4 py-8 text-center hover:border-skutopia-500 hover:bg-skutopia-50/40 dark:hover:bg-gray-800 transition-colors">
                          <FileText className="h-7 w-7 text-skutopia-600" />
                          <span className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200">
                            Choose PDF from device
                          </span>
                          <span className="text-[11px] text-gray-500">
                            PDF up to 15MB
                          </span>
                          <input
                            type="file"
                            accept="application/pdf,.pdf"
                            className="hidden"
                            onChange={handleBrochureSelect}
                          />
                        </label>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Faculty{" "}
                        <span className="font-normal text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        name="faculty_info"
                        value={formData.faculty_info}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="e.g., Led by Dr. Amina Phiri (Dean of Engineering) with instructors from industry and research."
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                      />
                      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                        Introduce key faculty, instructors, or academic leadership.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Application Dependency
                      </label>
                      <input
                        type="text"
                        name="application_dependency"
                        value={formData.application_dependency}
                        onChange={handleInputChange}
                        placeholder="e.g., Requires recommendation letter"
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Application Deadline
                      </label>
                      <input
                        type="date"
                        name="application_deadline"
                        value={formData.application_deadline}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                      />
                    </div>

                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3 md:p-4 space-y-3">
                      <div>
                        <p className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200">
                          How students apply
                        </p>
                        <p className="mt-1 text-[11px] md:text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                          Use these two links differently. Application URL controls
                          the Apply button. External URL is optional info only
                          (program page, brochure, or website).
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Application URL{" "}
                          <span className="font-normal text-gray-400">
                            (optional)
                          </span>
                        </label>
                        <input
                          type="url"
                          name="application_url"
                          value={formData.application_url}
                          onChange={handleInputChange}
                          placeholder="Leave blank to collect applications in Skutopia"
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                        <ul className="mt-1.5 space-y-1 text-[11px] text-gray-500 dark:text-gray-400 list-disc pl-4">
                          <li>
                            <strong className="font-medium text-gray-600 dark:text-gray-300">
                              Leave blank
                            </strong>{" "}
                            — students apply inside Skutopia. You receive their
                            Admissions Profile, academic details, and documents in
                            Applications.
                          </li>
                          <li>
                            <strong className="font-medium text-gray-600 dark:text-gray-300">
                              Paste an apply link
                            </strong>{" "}
                            — Apply opens that external form (Google Form,
                            Common App, your portal, etc.). Applications will not
                            land in Skutopia.
                          </li>
                        </ul>
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          External URL{" "}
                          <span className="font-normal text-gray-400">
                            (optional — Learn more)
                          </span>
                        </label>
                        <input
                          type="url"
                          name="external_url"
                          value={formData.external_url}
                          onChange={handleInputChange}
                          placeholder="https://your-program-page.edu/..."
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                        />
                        <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                          Shown as “Learn more on the partner site.” Does not
                          change how Apply works. Use for your program homepage,
                          eligibility details, or brochure — even when students
                          apply in Skutopia.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Cover image
                      </label>
                      <p className="mb-2 text-[11px] text-gray-500 dark:text-gray-400">
                        Upload from your device (JPG, PNG, WEBP, or GIF · max 5MB).
                        Shown on Discover and the program detail page.
                      </p>
                      {imagePreview ? (
                        <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                          <img
                            src={imagePreview}
                            alt="Program cover preview"
                            className="h-40 w-full object-cover"
                          />
                          <div className="absolute top-2 right-2 flex gap-2">
                            <label className="cursor-pointer rounded-lg bg-white/95 px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow-sm hover:bg-white">
                              Replace
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={handleProgramImageSelect}
                              />
                            </label>
                            <button
                              type="button"
                              onClick={clearProgramImage}
                              className="rounded-lg bg-white/95 p-1.5 text-gray-700 shadow-sm hover:bg-white"
                              aria-label="Remove cover image"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {imageFile && (
                            <p className="px-3 py-2 text-[11px] text-gray-500 dark:text-gray-400 bg-white/80 dark:bg-gray-900/70">
                              New image selected — it uploads when you submit.
                            </p>
                          )}
                        </div>
                      ) : (
                        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 px-4 py-8 text-center hover:border-skutopia-500 hover:bg-skutopia-50/40 dark:hover:bg-gray-800 transition-colors">
                          <ImagePlus className="h-7 w-7 text-skutopia-600" />
                          <span className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200">
                            Choose image from device
                          </span>
                          <span className="text-[11px] text-gray-500">
                            JPG, PNG, WEBP, or GIF up to 5MB
                          </span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                            onChange={handleProgramImageSelect}
                          />
                        </label>
                      )}
                    </div>

                    <div className="flex justify-end pt-3 md:pt-4">
                      <Button
                        onClick={() => setStep(2)}
                        className="bg-skutopia-600 hover:bg-skutopia-700 text-white text-xs md:text-sm"
                      >
                        Next Step
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-4 md:p-8">
                  <h2 className="text-base md:text-xl font-semibold text-gray-900 dark:text-white mb-4 md:mb-6">
                    Program Details
                  </h2>
                  <div className="space-y-6 md:space-y-8">
                    {/* Long Description */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                        Full Description
                      </label>
                      <textarea
                        name="long_description"
                        value={formData.long_description}
                        onChange={handleInputChange}
                        placeholder="Detailed description of your program"
                        rows={4}
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                      />
                    </div>

                    {/* Benefits */}
                    <div>
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <label className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                          Benefits
                        </label>
                        <Button
                          variant="ghost"
                          onClick={addBenefit}
                          className="text-skutopia-600 hover:text-skutopia-700 text-xs md:text-sm"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Benefit
                        </Button>
                      </div>
                      <div className="space-y-2 md:space-y-3">
                        {benefits.map((benefit, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              value={benefit}
                              onChange={(e) => updateBenefit(index, e.target.value)}
                              placeholder="e.g., Full scholarship covering tuition"
                              className="flex-1 px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                            />
                            <Button
                              variant="ghost"
                              onClick={() => removeBenefit(index)}
                              className="text-red-600 hover:text-red-700 text-xs"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Eligibility Requirements */}
                    <div>
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <label className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                          Eligibility Requirements
                        </label>
                        <Button
                          variant="ghost"
                          onClick={addRequirement}
                          className="text-skutopia-600 hover:text-skutopia-700 text-xs md:text-sm"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Requirement
                        </Button>
                      </div>
                      <div className="space-y-2 md:space-y-3">
                        {requirements.map((req, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              value={req}
                              onChange={(e) => updateRequirement(index, e.target.value)}
                              placeholder="e.g., Must be a current high school student"
                              className="flex-1 px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                            />
                            <Button
                              variant="ghost"
                              onClick={() => removeRequirement(index)}
                              className="text-red-600 hover:text-red-700 text-xs"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Application Requirements — shared catalog with Admissions Profile */}
                    <div>
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <div>
                          <label className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                            Application Requirements
                          </label>
                          <p className="text-[11px] text-gray-500 mt-0.5 max-w-md">
                            Choose from the same components learners fill in their
                            Admissions Profile. Use Other for a custom ask.
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          onClick={addApplicationRequirement}
                          className="text-skutopia-600 hover:text-skutopia-700 text-xs md:text-sm shrink-0"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Requirement
                        </Button>
                      </div>
                      <div className="space-y-2 md:space-y-3">
                        {applicationRequirements.map((req, index) => (
                          <div key={index} className="space-y-2">
                            <div className="flex gap-2">
                              <select
                                value={req.key}
                                onChange={(e) =>
                                  updateApplicationRequirementKey(
                                    index,
                                    e.target.value
                                  )
                                }
                                className="flex-1 px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                              >
                                {APPLICATION_REQUIREMENT_OPTIONS.map((opt) => (
                                  <option key={opt.key} value={opt.key}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  removeApplicationRequirement(index)
                                }
                                className="text-red-600 hover:text-red-700 text-xs"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            {req.key === APPLICATION_REQUIREMENT_OTHER && (
                              <input
                                type="text"
                                value={req.label || ""}
                                onChange={(e) =>
                                  updateApplicationRequirementLabel(
                                    index,
                                    e.target.value
                                  )
                                }
                                placeholder="Describe what you need (e.g. Portfolio PDF)"
                                className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-skutopia-500 focus:border-skutopia-500 text-xs md:text-sm"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between pt-3 md:pt-4">
                      <Button
                        variant="ghost"
                        onClick={() => setStep(1)}
                        className="text-gray-600 dark:text-gray-300 text-xs md:text-sm"
                      >
                        Previous
                      </Button>
                      <Button
                        onClick={() => setStep(3)}
                        className="bg-skutopia-600 hover:bg-skutopia-700 text-white text-xs md:text-sm"
                      >
                        Next Step
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-4 md:p-8">
                  <h2 className="text-base md:text-xl font-semibold text-gray-900 dark:text-white mb-4 md:mb-6">
                    Review & Submit
                  </h2>
                  <div className="space-y-4 md:space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 md:p-6">
                      <h3 className="text-sm md:font-medium text-gray-900 dark:text-white mb-3 md:mb-4">
                        Preview your program listing
                      </h3>
                      <div className="space-y-1.5 text-xs md:text-sm text-gray-600 dark:text-gray-300">
                        <p>
                          <strong>Title:</strong> {formData.title}
                        </p>
                        <p>
                          <strong>Description:</strong> {formData.description}
                        </p>
                        <p>
                          <strong>Location:</strong> {formData.location}
                        </p>
                        <p>
                          <strong>Funding Type:</strong> {formData.funding_type}
                        </p>
                        {benefits.filter((b) => b.trim()).length > 0 && (
                          <div>
                            <strong>Benefits:</strong>
                            <ul className="list-disc list-inside ml-1 md:ml-2">
                              {benefits
                                .filter((b) => b.trim())
                                .map((b, i) => (
                                  <li key={i}>{b}</li>
                                ))}
                            </ul>
                          </div>
                        )}
                        {serializeApplicationRequirements(applicationRequirements)
                          .length > 0 && (
                          <div>
                            <strong>Application requirements:</strong>
                            <ul className="list-disc list-inside ml-1 md:ml-2">
                              {serializeApplicationRequirements(
                                applicationRequirements
                              ).map((r, i) => (
                                <li key={i}>
                                  {
                                    APPLICATION_REQUIREMENT_OPTIONS.find(
                                      (o) => o.key === r.key
                                    )?.label || r.label || r.key
                                  }
                                  {r.key === APPLICATION_REQUIREMENT_OTHER &&
                                  r.label
                                    ? `: ${r.label}`
                                    : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between pt-3 md:pt-4">
                      <Button
                        variant="ghost"
                        onClick={() => setStep(2)}
                        className="text-gray-600 dark:text-gray-300 text-xs md:text-sm"
                      >
                        Previous
                      </Button>
                      <Button
                        onClick={handleSubmit}
                        disabled={loading || imageUploading || brochureUploading}
                        className="bg-skutopia-600 hover:bg-skutopia-700 text-white text-xs md:text-sm"
                      >
                        {imageUploading
                          ? "Uploading image..."
                          : brochureUploading
                            ? "Uploading brochure..."
                            : loading
                              ? "Submitting..."
                              : "Submit Program"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
      </div>
  );
};

export default PartnerAddProgram;
